import React, { useMemo } from 'react';
import {
  useTable,
  useSortBy,
  useRowSelect,
  useExpanded,
  Column,
  CellProps,
  HeaderGroup,
  Row,
  ColumnInstance,
} from 'react-table';
import cx from 'classnames';

import Checkbox from '../Checkbox';
import IconButton from '../IconButton';
import Text from '../Text';
import Table from '.';
import ChevronDownS1 from '../Icons/ChevronDownS1';
import ChevronUpS1 from '../Icons/ChevronUpS1';
import LoadingSpinnerNoBits from '../LoadingSpinnerNoBits';

import useUniqueId from '../utils/useUniqueId';

import styles from './TableGroup.module.scss';

export interface Props {
  /** Headers rendered within the table. */
  columns: Array<Column>;
  /** Accessible name for the Table, which will be visually hidden by default. */
  label: string;
  /** Data rendered within the table. */
  rows: Array<Row>;
  /** Call to action section that is typically an `IconButton` which would be appended at the end of row. */
  actions?: (
    getAllTableProps: React.PropsWithChildren<CellProps<object>>,
  ) => JSX.Element | undefined;
  /** Override table styles by adding a className. */
  className?: Parameters<typeof cx>[0];
  /** If true, the generic error state will appear. */
  isError?: boolean;
  /** If true, a loading spinner will appear and the table will not be interactive. */
  isLoading?: boolean;
  /** The value will render checkboxes and/or row expand toggle. */
  type?: 'default' | 'selectable' | 'expandable' | 'selectable-expandable';
  /** Content to be rendered in expanded row.  */
  expandedRow?: (row: Row<object>) => React.ReactNode;
}

function hideColumns(currentType: Props['type'], hasActions: Props['actions']) {
  const typeHiddenColumnENUM = {
    default: ['selection', 'expander'],
    selectable: ['expander'],
    expandable: ['selection'],
    'selectable-expandable': [],
  };

  return [
    ...typeHiddenColumnENUM[currentType || 'default'],
    hasActions === undefined ? 'actions' : '',
  ];
}

export const TableGroup: React.FC<Props> = ({
  className,
  columns,
  rows: propRows,
  label,
  isError = false,
  isLoading = false,
  type = 'default',
  actions = undefined,
  expandedRow,
}) => {
  const isEmpty = propRows.length <= 0;

  const memoColumns = useMemo(() => columns, [columns]);
  const memoRows = useMemo(() => propRows, [propRows]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    visibleColumns,
  } = useTable(
    {
      columns: memoColumns,
      data: memoRows,
      initialState: {
        pageIndex: 0,
        hiddenColumns: hideColumns(type, actions),
      },
    },
    useSortBy,
    useExpanded,
    useRowSelect,
    hooks => {
      hooks.visibleColumns.push((currentColumns: Array<Column>) => {
        return [
          {
            id: 'expander',
            Header: ' ',
            disableSortBy: true,
            Cell: ({ row, toggleAllRowsExpanded }) => {
              return (
                <IconButton
                  {...row.getToggleRowExpandedProps()}
                  onClick={() => {
                    if (row.isExpanded === undefined) {
                      toggleAllRowsExpanded(false);
                    }
                    row.toggleRowExpanded();
                  }}
                  accessibilityLabel={`Toggle expand row ${row.index + 1} of ${
                    rows.length
                  }, currently ${row.isExpanded ? 'expanded' : 'collapsed'}.`}
                  icon={row.isExpanded ? <ChevronUpS1 /> : <ChevronDownS1 />}
                  className={styles.expandBtn}
                />
              );
            },
          },
          {
            id: 'selection',
            disableSortBy: true,
            Header: row => {
              const {
                checked,
                title,
                ...restProps
              } = row.getToggleAllRowsSelectedProps();

              return (
                <Checkbox
                  {...restProps}
                  id={useUniqueId()}
                  value={checked || false}
                  hideLabel={true}
                  className={styles.checkbox}
                  onChange={() => row.toggleAllRowsSelected()}
                >
                  Select all rows on this table.
                </Checkbox>
              );
            },
            Cell: ({ row: { getToggleRowSelectedProps, index, id } }) => {
              const {
                checked,
                title,
                onChange,
                ...restProps
              } = getToggleRowSelectedProps();

              // TODO: Explore announcement of the number of selected rows after onChange: https://jira.plaid.com/browse/THREAD-910
              return (
                <Checkbox
                  id={useUniqueId(id)}
                  value={checked || false}
                  onChange={event => onChange && onChange(event)}
                  hideLabel={true}
                  className={styles.checkbox}
                  {...restProps}
                >
                  {`${title} row ${index + 1} of ${rows.length}`}
                </Checkbox>
              );
            },
          },
          ...currentColumns,
          {
            id: 'actions',
            Header: () => <span className={cx(styles.actions)}>Actions</span>,
            disableSortBy: true,
            Cell: actions === undefined ? '' : actions,
          },
        ];
      });
    },
  );

  if (isLoading) {
    return (
      <div className={styles.loadingGroup}>
        <LoadingSpinnerNoBits className={styles.loader} />
        <Text as='h2' size={16} weight='semiBold' className={styles.title}>
          Loading
        </Text>
      </div>
    );
  }
  // TODO: Replace with emptyState component when released
  // https://jira.plaid.com/browse/THREAD-876
  if (isError) {
    return (
      <div className={styles.unknownState}>
        <div className={styles.group}>
          <Text as='h2' size={16} weight='semiBold' className={styles.title}>
            Something went wrong
          </Text>
          <Text>
            There seems to be nothing that we have found. Please check back
            later or try creating something new.
          </Text>
        </div>
      </div>
    );
  }

  return (
    <>
      <Table
        {...getTableProps()}
        className={cx(styles.tableGroup, isEmpty && styles.isEmpty, className)}
        label={label}
      >
        <Table.Head>
          {headerGroups.map((headerGroup, i) => {
            return (
              <Table.Row {...headerGroup.getHeaderGroupProps()} key={i}>
                {headerGroup.headers.map(
                  (
                    column: HeaderGroup<object> & {
                      align?: 'start' | 'center' | 'end';
                      setWidth?: string;
                    },
                  ) => {
                    return (
                      <Table.HeadCell
                        {...column.getHeaderProps(
                          column.getSortByToggleProps(),
                        )}
                        disableSortBy={column.disableSortBy || false}
                        sort={
                          column.isSorted
                            ? column.isSortedDesc
                              ? 'descending'
                              : 'ascending'
                            : 'none'
                        }
                        key={column.id}
                        align={column?.align}
                        width={column?.setWidth}
                        className={cx(
                          column.id === 'selection' && styles.checkboxColumn,
                          column.id === 'expander' && styles.expanderColumn,
                          column.id === 'actions' && styles.actionsColumn,
                        )}
                      >
                        {column.render('Header')}
                      </Table.HeadCell>
                    );
                  },
                )}
              </Table.Row>
            );
          })}
        </Table.Head>
        <Table.Body {...getTableBodyProps()}>
          {rows.map(row => {
            prepareRow(row);
            return (
              <React.Fragment key={row.id}>
                <Table.Row {...row.getRowProps()} id={row.id}>
                  {row.cells.map(
                    (
                      {
                        getCellProps,
                        column,
                        render,
                      }: {
                        getCellProps: any;
                        column: ColumnInstance<object> & {
                          font?: 'sans' | 'monospace';
                          align?: 'start' | 'center' | 'end';
                          renderDataCell?: (
                            cell: React.ReactNode,
                          ) => JSX.Element;
                        };
                        render: (
                          type: string,
                          userProps?: object | undefined,
                        ) => React.ReactNode;
                      },
                      i,
                    ) => {
                      return (
                        <Table.Cell
                          {...getCellProps}
                          key={column.id + i}
                          font={
                            column.font === 'monospace' ? 'monospace' : 'sans'
                          }
                          align={column?.align}
                          cellWrapperClassName={cx(
                            column.id === 'expander' && styles.expanderWrapper,
                            column.id === 'actions' && styles.actionsWrapper,
                          )}
                          className={cx(
                            column.id === 'actions' && styles.actionsCell,
                          )}
                        >
                          {column.renderDataCell
                            ? column.renderDataCell(render('Cell'))
                            : render('Cell')}
                        </Table.Cell>
                      );
                    },
                  )}
                </Table.Row>
                <Table.RowExpanded
                  isExpanded={row.isExpanded}
                  colSpan={visibleColumns.length}
                  cellWrapperClassName={cx(
                    type === 'selectable-expandable' &&
                      styles.isSelectableAndExpandable,
                    type === 'expandable' && styles.isExpandable,
                  )}
                >
                  {expandedRow && expandedRow(row)}
                </Table.RowExpanded>
              </React.Fragment>
            );
          })}
        </Table.Body>
      </Table>
      {isEmpty && (
        <div className={cx(styles.unknownState)}>
          <div className={styles.group}>
            <Text as='h2' size={16} weight='semiBold' className={styles.title}>
              Nothing found, please try again
            </Text>
            <Text>Please check try creating something new.</Text>
          </div>
        </div>
      )}
    </>
  );
};

TableGroup.displayName = 'TableGroup';
export default TableGroup;
export { styles };
